import { useEffect, useRef } from 'react';

/**
 * Focus an element once it appears, without the `autoFocus` attribute.
 *
 * WHY THIS EXISTS (#793/#149). `jsx-a11y/no-autofocus` bans the `autoFocus` attribute,
 * and it is right to: on a page load it yanks a screen-reader user out of the document
 * they were about to read. But most of this app's uses are NOT page load — they are a
 * dialog the user just opened or an inline editor they just clicked into, where moving
 * focus is exactly the correct, accessible behaviour. Suppressing the rule would have
 * been the easy way through and would have thrown away the cases where it is right.
 *
 * Programmatic focus, in response to the thing appearing, is the pattern the rule's own
 * documentation recommends. It is also STRICTER than `autoFocus`, for two reasons worth
 * stating because they are why this is not merely a lint workaround:
 *
 *   1. `autoFocus` fires during mount. MUI's Dialog ALSO moves focus during mount (its
 *      focus trap pulls focus to the dialog container), so the two race — which is why
 *      an autoFocused field in a transitioning dialog sometimes ends up unfocused. A
 *      requestAnimationFrame defers past the transition's own focus move, so this wins
 *      deterministically.
 *   2. React only honours `autoFocus` on the initial mount of that element. An element
 *      that appears because a flag flipped (an inline editor toggling on) is often NOT
 *      a fresh mount, and `autoFocus` silently does nothing. Keying the effect on
 *      `active` handles that case, which `autoFocus` cannot.
 *
 * Usage:
 *   const focusRef = useAutoFocus();              // focus as soon as it mounts
 *   const focusRef = useAutoFocus(isEditing);     // focus each time it becomes active
 *
 *   <TextField inputRef={focusRef} … />           // MUI: the DOM node is behind inputRef
 *   <input ref={focusRef} … />                    // plain elements: ref
 *
 * NOT for use on a destructive confirm button. Focus belongs on the safe default there,
 * and MUI's Dialog already provides it — see ConfirmDialog/DeleteDialog, where an
 * autoFocused "Delete" meant one Enter destroyed the item.
 *
 * @param {boolean} active - focus when this becomes true (default: on mount).
 * @returns {React.RefObject} attach to `inputRef` (MUI) or `ref` (plain DOM).
 */
export default function useAutoFocus(active = true) {
  const ref = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    // rAF rather than a bare focus() call: see (1) above. Cancelled on cleanup so a
    // dialog closed within the same frame does not steal focus on its way out.
    const handle = requestAnimationFrame(() => {
      const node = ref.current;
      if (node && typeof node.focus === 'function') node.focus();
    });
    return () => cancelAnimationFrame(handle);
  }, [active]);

  return ref;
}
